import { useEffect, useState } from "react";
import { Sparkles, Zap, MessageSquare, Lightbulb, Pencil, Flame, BarChart3, Moon, Coins, Palmtree, ShoppingBag, Map, Bell } from "lucide-react";
import { BUSINESS_LABEL } from "../data/businesses";
import { trackEvent } from "../lib/analytics";
import { getRachaDisplay, registerRachaActivity } from "../lib/racha";
import { getVentaHoy } from "../lib/ventas";
import { FECHAS_COMERCIALES } from "../data/fechasComerciales";
import {
  shouldAskPermission,
  enableNotifications,
  declineNotifications,
  ensureScheduled,
} from "../lib/pushNotifications";

// Parse a "YYYY-MM-DD" string into a LOCAL Date (avoids UTC offset surprises).
function parseYMD(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Approximate coordinates per profile city for the weather lookup.
// Unknown cities fall back to CDMX.
const CITY_COORDS = {
  Acapulco: [16.86, -99.88],
  "Ciudad de México": [19.43, -99.13],
  Guadalajara: [20.66, -103.35],
  Monterrey: [25.67, -100.31],
  Puebla: [19.04, -98.2],
  Mérida: [20.97, -89.62],
};
const DEFAULT_COORDS = [19.43, -99.13];

// Payday ("quincena") detection — pure JS, no external API.
// A quincena lands on the 1st and 15th of each month; if that date falls on a
// Saturday or Sunday, businesses get paid the previous Friday. We show the
// banner when an (adjusted) payday is tomorrow or the day after tomorrow.
function adjustToPrevFriday(d) {
  const day = d.getDay(); // 0=Sun … 6=Sat
  if (day === 6) d.setDate(d.getDate() - 1); // Sat → Fri
  else if (day === 0) d.setDate(d.getDate() - 2); // Sun → Fri
  return d;
}

function sameYMD(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function quincenaEnProximosDos(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const dayAfter = new Date(today);
  dayAfter.setDate(today.getDate() + 2);
  const y = today.getFullYear();
  const m = today.getMonth();
  const candidates = [
    new Date(y, m, 1),
    new Date(y, m, 15),
    new Date(y, m + 1, 1), // covers the month boundary
  ];
  return candidates.some((c) => {
    const eff = adjustToPrevFriday(new Date(c));
    return sameYMD(eff, tomorrow) || sameYMD(eff, dayAfter);
  });
}

// Landing screen for users who already saved a perfil in localStorage.
// Replaces Screen1Selector as the entry point (Screen1 remains reachable via
// the "Tengo un problema específico" button — nothing is deleted).
export default function QuickStart({ perfil, onMenuEngineering, onQuickPublication, onFullFlow, onEditPerfil, onMisVentas, onCompetencia }) {
  const giroLabel = BUSINESS_LABEL[perfil.giro] || "negocio";

  // Snapshot the streak on mount (before this session's activity is registered)
  // so the "¡No pierdas tu racha!" nudge can appear when a live streak hasn't
  // been continued yet this week.
  const racha = getRachaDisplay();

  // Evening reminder: after 6pm, if today's sales haven't been logged yet,
  // nudge the owner to jot them down before the day ends.
  const showVentasReminder = getVentaHoy() == null && new Date().getHours() >= 18;

  // Payday banner: quincena tomorrow or the day after.
  const showQuincenaBanner = quincenaEnProximosDos();

  // National holiday banner — fetched from date.nager.at on load. Shows only
  // when there's a holiday within the next 7 days AND the quincena banner is
  // not active (one at a time). If the call fails we simply show nothing.
  const [holiday, setHoliday] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch("https://date.nager.at/api/v3/NextPublicHolidays/MX")
      .then((r) => (r.ok ? r.json() : null))
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const limit = new Date(today);
        limit.setDate(today.getDate() + 7);
        const found = list.find((h) => {
          const d = parseYMD(h.date);
          return d >= today && d <= limit;
        });
        if (found) setHoliday(found);
      })
      .catch(() => {
        /* offline / API down — just don't show the banner */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showHolidayBanner = !showQuincenaBanner && holiday != null;
  const holidayDate = holiday ? parseYMD(holiday.date) : null;
  const holidayDateLabel = holidayDate
    ? `${holidayDate.getDate()}/${holidayDate.getMonth() + 1}`
    : "";
  const holidayName = holiday ? holiday.localName || holiday.name : "";

  // Commercial date banner — our own list of Mexican sales opportunities that
  // aren't official holidays. Shows when one is within the next 5 days.
  // Priority: quincena > festivo oficial > fecha comercial (one at a time).
  const commercial = (() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const limit = new Date(today);
    limit.setDate(today.getDate() + 5);
    return (
      FECHAS_COMERCIALES.find((f) => {
        const d = parseYMD(f.date);
        return d >= today && d <= limit;
      }) || null
    );
  })();
  const showCommercialBanner =
    !showQuincenaBanner && !showHolidayBanner && commercial != null;
  const commercialDate = commercial ? parseYMD(commercial.date) : null;
  const commercialDateLabel = commercialDate
    ? `${commercialDate.getDate()}/${commercialDate.getMonth() + 1}`
    : "";

  // Weather context — Open-Meteo. If rain probability > 60% today or tomorrow,
  // suggest a WhatsApp promo. If the call fails, show nothing.
  const [rainSoon, setRainSoon] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const [lat, lon] = CITY_COORDS[perfil.ciudad] || DEFAULT_COORDS;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=precipitation_probability_max&timezone=America/Mexico_City&forecast_days=2`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const probs = data?.daily?.precipitation_probability_max;
        if (Array.isArray(probs) && probs.some((p) => typeof p === "number" && p > 60)) {
          setRainSoon(true);
        }
      })
      .catch(() => {
        /* offline / API down — just don't show the line */
      });
    return () => {
      cancelled = true;
    };
  }, [perfil.ciudad]);

  // PWA notifications: on first load (with profile saved) offer to enable push
  // reminders. If already granted, re-arm the schedule. Declines suppress 30d.
  const [showNotifPrompt, setShowNotifPrompt] = useState(false);
  useEffect(() => {
    if (shouldAskPermission()) {
      setShowNotifPrompt(true);
    } else {
      ensureScheduled(perfil);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnableNotif = async () => {
    setShowNotifPrompt(false);
    await enableNotifications(perfil);
  };

  const handleDeclineNotif = () => {
    setShowNotifPrompt(false);
    declineNotifications();
  };

  // Banner simple de avisos al final del contenido (independiente del pre-prompt).
  const [avisoConfirmed, setAvisoConfirmed] = useState(false);
  let avisoActivo = false;
  try {
    avisoActivo = localStorage.getItem("notificaciones_activas") === "true";
  } catch {
    avisoActivo = false;
  }
  const handleActivarAvisos = async () => {
    try {
      if (typeof Notification === "undefined") return;
      const res = await Notification.requestPermission();
      if (res === "granted") {
        try {
          localStorage.setItem("notificaciones_activas", "true");
        } catch {
          /* ignore */
        }
        setAvisoConfirmed(true);
      }
    } catch {
      /* ignore */
    }
  };

  const handleClick = (action, cb) => {
    trackEvent("quick_start_action", { action, giro: perfil.giro });
    registerRachaActivity();
    cb();
  };

  return (
    <div
      className="flex flex-col min-h-[100dvh] px-6 pt-10 pb-10 animate-fade-in-up"
      data-testid="quick-start-screen"
    >
      {/* Pre-prompt de notificaciones PWA */}
      {showNotifPrompt && (
        <div
          className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-slate-900/50 px-4 pb-6 sm:pb-0"
          data-testid="notif-prompt"
        >
          <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-500 text-white mb-4">
              <Bell className="w-6 h-6" strokeWidth={2.4} />
            </div>
            <p className="text-base font-bold text-slate-900 leading-snug">
              NegoIA puede avisarte cuando hay una oportunidad para tu negocio. ¿Activamos?
            </p>
            <div className="flex flex-col gap-2 mt-5">
              <button
                type="button"
                onClick={handleEnableNotif}
                data-testid="notif-enable-button"
                className="w-full py-3 rounded-xl text-sm font-bold bg-orange-500 text-white active:bg-orange-600 active:scale-[0.98] transition-all"
              >
                Sí, activar
              </button>
              <button
                type="button"
                onClick={handleDeclineNotif}
                data-testid="notif-decline-button"
                className="w-full py-3 rounded-xl text-sm font-bold bg-slate-100 text-slate-600 active:bg-slate-200 transition-all"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand */}
      <div className="flex items-center gap-2 mb-1">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-orange-500 text-white shadow-md shadow-orange-500/30">
          <Sparkles className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <span className="text-lg font-bold tracking-tight text-slate-900">
          Nego<span className="text-orange-500">IA</span>
        </span>
      </div>

      {/* Header */}
      <h1
        className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight mt-8"
        data-testid="quick-start-greeting"
      >
        ¡Hola, <span className="text-orange-500">{perfil.nombreNegocio}</span>! 👋
      </h1>
      <p
        className="text-sm text-slate-500 mt-2 leading-relaxed"
        data-testid="quick-start-subtitle"
      >
        {giroLabel} · {perfil.ciudad}
      </p>

      {/* Racha semanal — consecutive-weeks streak */}
      {racha.activeThisWeek && racha.weeks > 0 ? (
        <div
          className="mt-3 inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200"
          data-testid="quick-start-racha-active"
        >
          <Flame className="w-4 h-4 text-orange-500" strokeWidth={2.5} />
          <span className="text-sm font-bold text-orange-600">
            {racha.weeks} {racha.weeks === 1 ? "semana seguida" : "semanas seguidas"}
          </span>
        </div>
      ) : racha.alive ? (
        <p
          className="mt-3 text-sm font-bold text-orange-500"
          data-testid="quick-start-racha-nudge"
        >
          ¡No pierdas tu racha!
        </p>
      ) : null}

      {/* Banner de quincena */}
      {showQuincenaBanner && (
        <button
          type="button"
          onClick={() => handleClick("quincena", onMenuEngineering)}
          data-testid="quincena-banner"
          className="mt-4 w-full text-left flex items-center gap-3 p-4 rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/20 shrink-0">
            <Coins className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <p className="text-sm font-bold leading-snug min-w-0">
            💰 Mañana es quincena — el mejor momento del mes para promocionar. ¿Generamos tu estrategia ahora?
          </p>
        </button>
      )}

      {/* Banner de festivo nacional */}
      {showHolidayBanner && (
        <button
          type="button"
          onClick={() => handleClick("festivo", onMenuEngineering)}
          data-testid="holiday-banner"
          className="mt-4 w-full text-left flex items-center gap-3 p-4 rounded-2xl bg-green-600 text-white shadow-lg shadow-green-600/30 active:bg-green-700 active:scale-[0.98] transition-all"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/20 shrink-0">
            <Palmtree className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <p className="text-sm font-bold leading-snug min-w-0">
            🏖️ El {holidayDateLabel} es {holidayName}. Los negocios que comunican con anticipación venden más. ¿Empezamos hoy?
          </p>
        </button>
      )}

      {/* Banner de fecha comercial (naranja, distinto al verde de festivos) */}
      {showCommercialBanner && (
        <button
          type="button"
          onClick={() => handleClick("fecha_comercial", onMenuEngineering)}
          data-testid="commercial-banner"
          className="mt-4 w-full text-left flex items-center gap-3 p-4 rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/20 shrink-0">
            <ShoppingBag className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <p className="text-sm font-bold leading-snug min-w-0">
            🛍️ El {commercialDateLabel} es {commercial.name}. Gran oportunidad para promocionar. ¿Generamos tu estrategia ahora?
          </p>
        </button>
      )}

      {/* Recordatorio nocturno de ventas */}
      {showVentasReminder && (
        <button
          type="button"
          onClick={() => handleClick("mis_ventas", onMisVentas)}
          data-testid="ventas-reminder-banner"
          className="mt-6 w-full text-left flex items-center gap-3 p-4 rounded-2xl bg-teal-50 border-2 border-teal-200 active:scale-[0.98] transition-all"
        >
          <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-teal-600 text-white shrink-0">
            <Moon className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-teal-900 leading-snug">
              🌙 ¿Cómo te fue hoy? Tarda 5 segundos.
            </p>
            <p className="text-xs text-teal-700 mt-0.5 leading-snug">
              Toca aquí para registrarla antes de que termine el día.
            </p>
          </div>
        </button>
      )}

      {/* Buttons */}
      <div className="flex flex-col gap-3 mt-10">
        {/* Botón 1 — principal */}
        <button
          type="button"
          onClick={() => handleClick("menu_engineering", onMenuEngineering)}
          data-testid="quick-menu-engineering-button"
          className="w-full text-left p-5 rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/30 active:bg-orange-600 active:scale-[0.98] transition-all"
        >
          <div className="flex items-start gap-3">
            <Zap className="w-6 h-6 shrink-0 mt-0.5" strokeWidth={2.5} />
            <div className="min-w-0">
              <p className="text-lg font-extrabold leading-tight">⚡ ¿Qué promociono hoy?</p>
              <p className="text-sm mt-1 opacity-95 leading-snug">
                Te digo qué vender para ganar más
              </p>
            </div>
          </div>
        </button>

        {/* Botón 2 — publicación */}
        <button
          type="button"
          onClick={() => handleClick("publicacion", onQuickPublication)}
          data-testid="quick-publication-button"
          className="w-full text-left p-5 rounded-2xl bg-white border-2 border-slate-200 active:border-teal-600 active:bg-slate-50 active:scale-[0.98] transition-all"
        >
          <div className="flex items-start gap-3">
            <MessageSquare className="w-6 h-6 text-teal-700 shrink-0 mt-0.5" strokeWidth={2.4} />
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 leading-tight">📱 Necesito una publicación</p>
              <p className="text-sm mt-1 text-slate-500 leading-snug">
                Texto listo para copiar en tus redes
              </p>
            </div>
          </div>
        </button>

        {/* Botón 3 — problema */}
        <button
          type="button"
          onClick={() => handleClick("problema", onFullFlow)}
          data-testid="quick-fullflow-button"
          className="w-full text-left p-5 rounded-2xl bg-white border-2 border-slate-200 active:border-teal-600 active:bg-slate-50 active:scale-[0.98] transition-all"
        >
          <div className="flex items-start gap-3">
            <Lightbulb className="w-6 h-6 text-teal-700 shrink-0 mt-0.5" strokeWidth={2.4} />
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 leading-tight">💡 Tengo un problema específico</p>
              <p className="text-sm mt-1 text-slate-500 leading-snug">
                Estrategia personalizada para tu reto de hoy
              </p>
            </div>
          </div>
        </button>
        {/* Botón 4 — Mis Ventas */}
        <button
          type="button"
          onClick={() => handleClick("mis_ventas", onMisVentas)}
          data-testid="quick-mis-ventas-button"
          className="w-full text-left p-5 rounded-2xl bg-white border-2 border-slate-200 active:border-teal-600 active:bg-slate-50 active:scale-[0.98] transition-all"
        >
          <div className="flex items-start gap-3">
            <BarChart3 className="w-6 h-6 text-teal-700 shrink-0 mt-0.5" strokeWidth={2.4} />
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 leading-tight">📊 ¿Cuánto vendí hoy?</p>
              <p className="text-sm mt-1 text-slate-500 leading-snug">
                Anota tus ventas y mira tu semana
              </p>
            </div>
          </div>
        </button>

        {/* Botón 5 — Ver mi competencia */}
        <button
          type="button"
          onClick={() => handleClick("ver_competencia", onCompetencia)}
          data-testid="quick-competencia-button"
          className="w-full text-left p-5 rounded-2xl bg-white border-2 border-slate-200 active:border-teal-600 active:bg-slate-50 active:scale-[0.98] transition-all"
        >
          <div className="flex items-start gap-3">
            <Map className="w-6 h-6 text-teal-700 shrink-0 mt-0.5" strokeWidth={2.4} />
            <div className="min-w-0">
              <p className="text-lg font-bold text-slate-900 leading-tight">🗺️ Ver mi competencia</p>
              <p className="text-sm mt-1 text-slate-500 leading-snug">
                Negocios similares cerca de ti en el mapa
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Línea de contexto de clima (debajo de los 4 botones) */}
      {rainSoon && (
        <button
          type="button"
          onClick={() => handleClick("clima_lluvia", onMenuEngineering)}
          data-testid="weather-line"
          className="mt-4 w-full text-left flex items-start gap-1.5 text-xs text-slate-500 leading-snug active:text-slate-700 transition-colors"
        >
          🌧️ Se pronostica lluvia en {perfil.ciudad}. ¿Activamos una promoción de WhatsApp para llegar a tus clientes sin que salgan?
        </button>
      )}

      {/* Botón 4 — editar perfil, texto simple */}
      <button
        type="button"
        onClick={() => handleClick("editar_perfil", onEditPerfil)}
        data-testid="quick-edit-perfil-button"
        className="mt-6 self-center inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 active:text-slate-800 py-2"
      >
        <Pencil className="w-4 h-4" strokeWidth={2.4} />
        ✏️ Actualizar mi negocio
      </button>

      {/* Banner de avisos al final del contenido */}
      {avisoConfirmed ? (
        <p
          data-testid="avisos-confirmacion"
          className="mt-6 text-center text-sm font-semibold text-teal-700"
        >
          ✓ Listo, te avisaremos.
        </p>
      ) : !avisoActivo ? (
        <div
          data-testid="avisos-banner"
          className="mt-6 flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200"
        >
          <p className="text-xs text-slate-600 leading-snug min-w-0">
            🔔 Activa avisos para no perderte oportunidades
          </p>
          <button
            type="button"
            onClick={handleActivarAvisos}
            data-testid="avisos-activar-button"
            className="shrink-0 px-4 py-2 rounded-lg text-xs font-bold bg-orange-500 text-white active:bg-orange-600 transition-all"
          >
            Activar
          </button>
        </div>
      ) : null}

      {/* Spacer so footer sits at bottom */}
      <div className="flex-1" />
    </div>
  );
}
